<script setup>
import { ref, computed, onMounted, nextTick, useTemplateRef } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useMapGetter } from 'dashboard/composables/store';
import { emitter } from 'shared/helpers/mitt';
import { MESSAGE_VARIABLES } from 'shared/constants/messages';
import MessageAPI from 'dashboard/api/inbox/message';
import ConversationAPI from 'dashboard/api/inbox/conversation';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import TimeAgo from 'components/ui/TimeAgo.vue';
import WootMessageEditor from 'dashboard/components/widgets/WootWriter/Editor.vue';

const PER_PAGE = 25;
const STEP_COMPLETED_NUMBER_BG = '#10b981';
const store = useStore();
const { t } = useI18n();

const loadingList = ref(false);
const listFetchId = ref(0);
const sendingMessage = ref(false);
const conversations = ref([]);
const totalConversations = ref(0);
const conversationPage = ref(1);
const composerMessage = ref('');

const selectedIds = ref(new Set());
const sendNow = ref(true);
const scheduledTime = ref('');
const messageVariations = ref([]);
const editingVariationIndex = ref(null);
const variationInput = ref('');

const customAttributes = useMapGetter('attributes/getAttributes');

const variablesDialogRef = useTemplateRef('variablesDialogRef');
const filtersDialogRef = useTemplateRef('filtersDialogRef');

const conversationFilters = ref({
  query: '',
  channel: '',
  label: '',
  priority: '',
});

const openVariablesModal = () => {
  nextTick(() => {
    variablesDialogRef.value?.open();
  });
};

const openFiltersModal = () => {
  nextTick(() => {
    filtersDialogRef.value?.open();
  });
};

const closeFiltersModal = () => {
  filtersDialogRef.value?.close();
};

const standardVariablesList = computed(() =>
  MESSAGE_VARIABLES.map(v => ({
    key: v.key,
    description: v.label,
  }))
);

const customVariablesList = computed(() =>
  (customAttributes.value || []).map(attribute => {
    const model = attribute.attribute_model ?? attribute.attributeModel ?? '';
    const prefix =
      model === 'conversation_attribute' ? 'conversation' : 'contact';
    const attrKey = attribute.attribute_key ?? attribute.attributeKey ?? '';
    const key = `${prefix}.custom_attribute.${attrKey}`;
    const description =
      attribute.attribute_description ?? attribute.attributeDescription ?? key;
    return { key, description };
  })
);

const isProcessing = ref(false);
const processedItems = ref([]);
const successCount = ref(0);
const errorCount = ref(0);
const progress = ref(0);

const plainTextFromHtml = html => {
  if (!html) return '';
  return html
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
};

const isComposerEmpty = computed(
  () => plainTextFromHtml(composerMessage.value) === ''
);

const isMessageBodyBlank = body => plainTextFromHtml(body) === '';

const conversationTitle = c => {
  const name = c.meta?.sender?.name;
  return name || `#${c.id}`;
};

const conversationInitial = c => {
  const title = conversationTitle(c);
  return title ? title.charAt(0).toUpperCase() : '#';
};

const conversationThumbnail = c => c.meta?.sender?.thumbnail;

const conversationLabels = c => (Array.isArray(c.labels) ? c.labels : []);

const priorityClasses = priority => {
  const classes = {
    urgent: 'bg-ruby-100 text-ruby-700 dark:bg-ruby-900/40 dark:text-ruby-300',
    high: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
    medium: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
    low: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
  };
  return classes[priority] || classes.low;
};

const priorityLabel = priority =>
  t(`KANBAN.PRIORITY_LABELS.${priority.toUpperCase()}`);

const conversationInboxName = c => {
  const inboxName = c.inbox?.name || c.inbox_name || c.inboxName;
  if (inboxName) return inboxName;

  const inbox = store.getters['inboxes/getInbox']?.(c.inbox_id);
  return inbox?.name || '—';
};

const uniqueOptions = list => [...new Set(list.filter(Boolean))].sort();

const channelFilterOptions = computed(() =>
  uniqueOptions(conversations.value.map(c => conversationInboxName(c)))
);

const labelFilterOptions = computed(() =>
  uniqueOptions(conversations.value.flatMap(c => conversationLabels(c)))
);

const priorityFilterOptions = computed(() =>
  uniqueOptions(conversations.value.map(c => c.priority))
);

const activeConversationFiltersCount = computed(
  () => Object.values(conversationFilters.value).filter(Boolean).length
);

const filteredConversations = computed(() => {
  const filters = conversationFilters.value;
  const term = filters.query.trim().toLowerCase();
  return conversations.value.filter(c => {
    if (filters.channel && conversationInboxName(c) !== filters.channel) {
      return false;
    }
    if (filters.label && !conversationLabels(c).includes(filters.label)) {
      return false;
    }
    if (filters.priority && c.priority !== filters.priority) {
      return false;
    }
    if (!term) return true;

    const fields = [conversationTitle(c), c.id, c.display_id];
    return fields.some(field => String(field || '').toLowerCase().includes(term));
  });
});

const clearConversationFilters = () => {
  conversationFilters.value = {
    query: '',
    channel: '',
    label: '',
    priority: '',
  };
};

const conversationPageCount = computed(() =>
  Math.max(1, Math.ceil(totalConversations.value / PER_PAGE))
);

const paginatedConversations = computed(() => filteredConversations.value);

const hasSelections = computed(
  () => selectedIds.value.size > 0 && conversations.value.length > 0
);

const allListedSelected = computed(
  () =>
    paginatedConversations.value.length > 0 &&
    paginatedConversations.value.every(c => selectedIds.value.has(c.id))
);

const messageBodies = computed(() => {
  const bodies = [];
  if (!isComposerEmpty.value) bodies.push(composerMessage.value);
  bodies.push(
    ...messageVariations.value.filter(body => !isMessageBodyBlank(body))
  );
  return bodies;
});

const canSendMessages = computed(
  () =>
    messageBodies.value.length > 0 &&
    hasSelections.value &&
    (sendNow.value || scheduledTime.value.trim() !== '')
);

const step1Done = computed(() => hasSelections.value);
const step2Done = computed(() => messageBodies.value.length > 0);
const step3Done = computed(
  () => sendNow.value || scheduledTime.value.trim() !== ''
);

const highlightedStep = computed(() => {
  if (!step1Done.value) return 1;
  if (!step2Done.value) return 2;
  if (!step3Done.value) return 3;
  return null;
});

const isCompleted = computed(
  () => progress.value === 100 && !sendingMessage.value
);

const buildListParams = page => ({ page, status: 'open' });

const fetchConversations = async (page = 1) => {
  const currentFetchId = listFetchId.value + 1;
  listFetchId.value = currentFetchId;
  loadingList.value = true;
  selectedIds.value = new Set();
  conversations.value = [];
  try {
    const response = await ConversationAPI.get(buildListParams(page));
    if (currentFetchId !== listFetchId.value) return;

    const payload = response.data?.data?.payload ?? [];
    const meta = response.data?.data?.meta ?? {};
    conversations.value = payload;
    conversationPage.value = page;
    totalConversations.value = meta.all_count ?? payload.length;
  } catch (error) {
    conversations.value = [];
    totalConversations.value = 0;
    emitter.emit('newToastMessage', {
      message: error?.message || t('CONVERSATION.FORWARD_MESSAGE.ERROR'),
      type: 'error',
    });
  } finally {
    if (currentFetchId === listFetchId.value) {
      loadingList.value = false;
    }
  }
};

const toggleId = id => {
  const next = new Set(selectedIds.value);
  if (next.has(id)) next.delete(id);
  else next.add(id);
  selectedIds.value = next;
};

const selectAllListed = () => {
  const visibleIds = paginatedConversations.value.map(c => c.id);
  if (allListedSelected.value) {
    const next = new Set(selectedIds.value);
    visibleIds.forEach(id => next.delete(id));
    selectedIds.value = next;
  } else {
    selectedIds.value = new Set([...selectedIds.value, ...visibleIds]);
  }
};

const previousConversationPage = () => {
  if (conversationPage.value <= 1 || loadingList.value) return;
  fetchConversations(conversationPage.value - 1);
};

const nextConversationPage = () => {
  if (
    conversationPage.value >= conversationPageCount.value ||
    loadingList.value
  ) {
    return;
  }
  fetchConversations(conversationPage.value + 1);
};

const handleAddVariation = () => {
  editingVariationIndex.value = messageVariations.value.length;
  variationInput.value = composerMessage.value;
};

const handleSaveVariation = () => {
  if (isMessageBodyBlank(variationInput.value)) return;
  if (
    editingVariationIndex.value !== null &&
    editingVariationIndex.value < messageVariations.value.length
  ) {
    messageVariations.value[editingVariationIndex.value] =
      variationInput.value;
  } else {
    messageVariations.value.push(variationInput.value);
  }
  editingVariationIndex.value = null;
  variationInput.value = '';
};

const handleCancelVariation = () => {
  editingVariationIndex.value = null;
  variationInput.value = '';
};

const handleEditVariation = index => {
  editingVariationIndex.value = index;
  variationInput.value = messageVariations.value[index];
};

const handleRemoveVariation = index => {
  messageVariations.value.splice(index, 1);
};

const handleSend = async () => {
  if (!canSendMessages.value) return;

  const bodies = messageBodies.value;
  if (bodies.length === 0) return;

  const targets = conversations.value.filter(c => selectedIds.value.has(c.id));

  isProcessing.value = true;
  sendingMessage.value = true;
  processedItems.value = [];
  successCount.value = 0;
  errorCount.value = 0;
  progress.value = 0;

  try {
    for (let i = 0; i < targets.length; i += 1) {
      const c = targets[i];
      try {
        const inboxId = c.inbox_id;
        const messageContent = bodies[i % bodies.length];
        if (sendNow.value) {
          await MessageAPI.create({
            conversationId: c.id,
            message: messageContent,
            private: false,
            message_type: 'outgoing',
            inbox_id: inboxId,
          });
        } else {
          const plain = plainTextFromHtml(messageContent);
          const title =
            plain.length > 50 ? `${plain.slice(0, 50)}…` : plain || '…';
          await ConversationAPI.scheduleMessage({
            conversation_id: c.id,
            inbox_id: inboxId,
            content: messageContent,
            scheduled_at: new Date(scheduledTime.value).toISOString(),
            title,
          });
        }
        processedItems.value.push({
          id: c.id,
          title: conversationTitle(c),
          conversationId: c.id,
          status: 'success',
        });
        successCount.value += 1;
      } catch (error) {
        processedItems.value.push({
          id: c.id,
          title: conversationTitle(c),
          conversationId: c.id,
          status: 'error',
          error: error?.message,
        });
        errorCount.value += 1;
      }
      progress.value = ((i + 1) / targets.length) * 100;
    }

    emitter.emit('newToastMessage', {
      message: sendNow.value
        ? t('MESSAGE_DISPATCHER.TOAST_SEND_DONE', {
            ok: successCount.value,
            fail: errorCount.value,
          })
        : t('MESSAGE_DISPATCHER.TOAST_SCHEDULE_DONE', {
            ok: successCount.value,
            fail: errorCount.value,
          }),
      type: successCount.value > 0 ? 'success' : 'error',
    });
  } catch {
    emitter.emit('newToastMessage', {
      message: t('CONVERSATION.FORWARD_MESSAGE.ERROR'),
      type: 'error',
    });
  } finally {
    sendingMessage.value = false;
  }
};

const finishProgress = () => {
  isProcessing.value = false;
  processedItems.value = [];
  successCount.value = 0;
  errorCount.value = 0;
  progress.value = 0;
};

onMounted(() => {
  store.dispatch('attributes/get');
  store.dispatch('inboxes/get');
  fetchConversations();
});
</script>

<template>
  <div class="flex h-full w-full min-h-0 flex-col overflow-hidden bg-n-surface-1">
    <header class="flex shrink-0 flex-wrap items-center justify-between gap-4 border-b border-n-weak px-4 py-4 md:px-6">
      <div class="space-y-1">
        <h1 class="text-xl font-semibold text-n-slate-12">
          {{ t('MESSAGE_DISPATCHER.TITLE') }}
        </h1>
        <p class="text-sm text-n-slate-11">
          {{ t('MESSAGE_DISPATCHER.DESCRIPTION') }}
        </p>
      </div>
      <div
        v-if="!isProcessing"
        class="flex flex-wrap items-center justify-end gap-4"
      >
        <div class="flex items-center gap-3">
          <span
            class="text-sm font-medium text-n-slate-12"
            :class="{ 'text-n-slate-10': sendNow }"
          >
            {{ t('MESSAGE_DISPATCHER.TOGGLE_SCHEDULE_LATER') }}
          </span>
          <Switch v-model="sendNow" />
          <span
            class="text-sm font-medium text-n-slate-12"
            :class="{ 'text-n-slate-10': !sendNow }"
          >
            {{ t('MESSAGE_DISPATCHER.TOGGLE_SCHEDULE_NOW') }}
          </span>
        </div>
        <Button
          variant="solid"
          color="blue"
          size="sm"
          :is-loading="sendingMessage"
          :disabled="!canSendMessages"
          @click="handleSend"
        >
          {{ t('KANBAN.BULK_ACTIONS.SEND_MESSAGES') }}
        </Button>
      </div>
    </header>

    <div class="flex min-h-0 flex-1 flex-col overflow-hidden p-4 md:p-6">
      <div v-if="isProcessing" class="min-h-0 flex-1 space-y-6 overflow-y-auto">
        <div
          class="rounded-2xl border border-n-weak bg-n-solid-1 p-6 shadow-sm"
        >
          <div class="text-center space-y-2">
            <p
              class="text-xs font-semibold uppercase tracking-wide"
              :class="isCompleted ? 'text-emerald-600' : 'text-n-brand'"
            >
              {{
                isCompleted
                  ? t('KANBAN.BULK_ACTIONS.SEND_MESSAGE.COMPLETED_BADGE')
                  : t('KANBAN.BULK_ACTIONS.SEND_MESSAGE.PROCESSING_BADGE')
              }}
            </p>
            <h3
              class="text-xl font-semibold text-n-slate-12"
              :class="isCompleted ? 'text-emerald-600' : ''"
            >
              {{
                isCompleted
                  ? t('MESSAGE_DISPATCHER.DONE_TITLE')
                  : t('MESSAGE_DISPATCHER.PROGRESS_TITLE')
              }}
            </h3>
            <p class="text-sm text-n-slate-11">
              {{
                t('MESSAGE_DISPATCHER.PROGRESS_HINT', {
                  count: processedItems.length,
                  total: selectedIds.size,
                })
              }}
            </p>
          </div>
          <div class="relative mt-6 space-y-3">
            <div
              class="flex items-center justify-between text-xs font-semibold"
            >
              <span
                class="inline-flex items-center rounded-full px-3 py-1"
                :class="
                  isCompleted
                    ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
                    : 'bg-n-alpha-2 text-n-brand'
                "
              >
                {{ Math.round(progress) }}%
              </span>
              <span class="text-n-slate-11">
                {{ successCount }}
                {{ t('KANBAN.BULK_ACTIONS.SEND_MESSAGE.SUCCESS') }} ·
                {{ errorCount }}
                {{ t('KANBAN.BULK_ACTIONS.SEND_MESSAGE.ERROR') }}
              </span>
            </div>
            <div class="h-3 overflow-hidden rounded-full bg-n-alpha-2">
              <div
                class="h-full rounded-full transition-all duration-500"
                :class="isCompleted ? 'bg-emerald-500' : 'bg-n-brand'"
                :style="{ width: `${progress}%` }"
              />
            </div>
          </div>
        </div>

        <div
          class="rounded-2xl border border-n-weak bg-n-solid-1 shadow-sm overflow-hidden"
        >
          <div
            class="flex items-center justify-between border-b border-n-weak px-6 py-4 text-sm"
          >
            <div class="font-medium text-n-slate-12">
              {{ t('KANBAN.BULK_ACTIONS.SEND_MESSAGE.PROCESSING_TITLE') }}
            </div>
            <span class="text-xs text-n-slate-11">
              {{
                t('KANBAN.BULK_ACTIONS.SEND_MESSAGE.PROCESSING_RESULTS', {
                  count: processedItems.length,
                })
              }}
            </span>
          </div>
          <div class="max-h-[430px] divide-y divide-n-weak overflow-y-auto">
            <div
              v-for="item in processedItems"
              :key="item.id"
              class="flex items-center gap-3 px-6 py-4 text-sm hover:bg-n-alpha-1"
            >
              <span
                class="flex h-10 w-10 items-center justify-center rounded-full"
                :class="
                  item.status === 'success'
                    ? 'bg-emerald-100 text-emerald-600 dark:bg-emerald-900/40'
                    : 'bg-ruby-100 text-ruby-600 dark:bg-ruby-900/40'
                "
              >
                <fluent-icon
                  :icon="
                    item.status === 'success'
                      ? 'checkmark-circle'
                      : 'error-circle'
                  "
                  size="18"
                />
              </span>
              <div class="flex-1 min-w-0">
                <p class="font-medium text-n-slate-12 truncate">
                  {{ item.title }}
                </p>
                <p class="text-xs text-n-slate-11">
                  {{
                    t('KANBAN.BULK_ACTIONS.SEND_MESSAGE.CONVERSATION_ID', {
                      id: item.conversationId,
                    })
                  }}
                </p>
                <p v-if="item.error" class="mt-2 text-xs text-ruby-600">
                  {{ item.error }}
                </p>
              </div>
              <span
                class="rounded-full px-3 py-1 text-xs font-semibold shrink-0"
                :class="
                  item.status === 'success'
                    ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'
                    : 'bg-ruby-100 text-ruby-700 dark:bg-ruby-900/40 dark:text-ruby-300'
                "
              >
                {{
                  item.status === 'success'
                    ? t('KANBAN.BULK_ACTIONS.SEND_MESSAGE.STATUS_SUCCESS')
                    : t('KANBAN.BULK_ACTIONS.SEND_MESSAGE.STATUS_ERROR')
                }}
              </span>
            </div>
          </div>
        </div>

        <div class="flex justify-end">
          <Button
            variant="ghost"
            color="slate"
            size="sm"
            @click="finishProgress"
          >
            {{ t('MESSAGE_DISPATCHER.CLOSE') }}
          </Button>
        </div>
      </div>

      <div
        v-else
        class="grid min-h-0 flex-1 gap-4 overflow-y-auto lg:grid-cols-[minmax(0,1fr)_minmax(28rem,34rem)] lg:overflow-hidden"
      >
        <section class="flex min-h-[24rem] flex-col overflow-hidden lg:min-h-0">
          <div class="flex flex-wrap items-center gap-3 border-b border-n-weak pb-3">
            <div class="flex min-h-8 min-w-0 shrink-0 items-center gap-2">
              <p class="mb-0 text-xs uppercase tracking-wide text-n-slate-11">
                {{ t('MESSAGE_DISPATCHER.CONVERSATIONS_SECTION') }}
              </p>
              <h4 class="mb-0 text-base font-semibold leading-5 text-n-slate-12 truncate">
                {{ t('MESSAGE_DISPATCHER.SELECT_CONVERSATIONS') }}
              </h4>
              <span
                class="inline-flex items-center rounded-full border border-n-weak bg-n-alpha-1 px-1.5 py-0.5 text-[10px] font-semibold text-n-slate-11 shrink-0"
              >
                {{ selectedIds.size }}/{{ totalConversations }}
              </span>
            </div>
            <div class="flex flex-1 justify-end">
              <Button
                variant="outline"
                color="slate"
                size="sm"
                icon="i-lucide-filter"
                @click="openFiltersModal"
              >
                {{ t('MESSAGE_DISPATCHER.FILTER_BUTTON') }}
                <span v-if="activeConversationFiltersCount">
                  ({{ activeConversationFiltersCount }})
                </span>
              </Button>
            </div>
          </div>

          <div class="min-h-0 flex-1 pt-4">
            <div
              v-if="loadingList && conversations.length === 0"
              class="flex justify-center py-8"
            >
              <span class="loading-spinner" />
            </div>
            <div
              v-if="filteredConversations.length > 0"
              class="flex h-full min-h-0 flex-col overflow-hidden"
            >
              <div class="min-h-0 flex-1 overflow-auto">
                <table class="w-full min-w-[960px] table-fixed border-collapse text-sm">
                  <colgroup>
                    <col class="w-[50px]" />
                    <col class="w-[44px]" />
                    <col />
                    <col class="w-[120px]" />
                    <col class="w-[180px]" />
                    <col class="w-[110px]" />
                    <col class="w-[120px]" />
                  </colgroup>
                  <thead class="sticky top-0 z-10 bg-slate-50 text-left font-medium text-slate-700 dark:bg-slate-700 dark:text-slate-300">
                    <tr class="border-b border-slate-200 dark:border-slate-700">
                      <th class="py-2 text-center font-medium">
                        <input
                          type="checkbox"
                          :checked="allListedSelected"
                          class="h-4 w-4 rounded border-slate-300 bg-white text-woot-500 focus:ring-woot-500 dark:border-slate-600 dark:bg-slate-800"
                          @change="selectAllListed"
                        >
                      </th>
                      <th class="py-2 font-medium">&nbsp;</th>
                      <th class="py-2 pr-2 font-medium">
                        {{ t('MESSAGE_DISPATCHER.CONVERSATION_COLUMN') }}
                      </th>
                      <th class="py-2 pr-2 font-medium">
                        {{ t('MESSAGE_DISPATCHER.CHANNEL_COLUMN') }}
                      </th>
                      <th class="py-2 pr-2 font-medium">
                        {{ t('MESSAGE_DISPATCHER.LABELS_COLUMN') }}
                      </th>
                      <th class="py-2 pr-2 font-medium">
                        {{ t('MESSAGE_DISPATCHER.PRIORITY_COLUMN') }}
                      </th>
                      <th class="py-2 pr-4 font-medium">
                        {{ t('MESSAGE_DISPATCHER.LAST_ACTIVITY_COLUMN') }}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-if="loadingList"
                      class="border-b border-slate-200 text-xs text-slate-500 dark:border-slate-700 dark:text-slate-400"
                    >
                      <td colspan="7" class="px-4 py-2">
                        <div class="flex items-center justify-between">
                          <span>{{ t('MESSAGE_DISPATCHER.LOADING_CONVERSATIONS') }}</span>
                          <span class="font-semibold text-slate-700 dark:text-slate-200">
                            {{ conversations.length }}
                          </span>
                        </div>
                      </td>
                    </tr>
                    <tr
                      v-for="c in paginatedConversations"
                      :key="c.id"
                      class="cursor-pointer border-b border-slate-200 transition last:border-b-0 hover:bg-slate-50 dark:border-slate-700 dark:hover:bg-slate-700"
                      @click="toggleId(c.id)"
                    >
                      <td class="py-2 text-center align-middle">
                        <input
                          type="checkbox"
                          :checked="selectedIds.has(c.id)"
                          class="h-4 w-4 rounded border-slate-300 bg-white text-woot-500 focus:ring-woot-500 dark:border-slate-600 dark:bg-slate-800"
                          @click.stop="toggleId(c.id)"
                        />
                      </td>
                      <td class="py-2 align-middle">
                        <img
                          v-if="conversationThumbnail(c)"
                          :src="conversationThumbnail(c)"
                          :alt="conversationTitle(c)"
                          class="h-8 w-8 rounded-full object-cover"
                        >
                        <div
                          v-else
                          class="flex h-8 w-8 items-center justify-center rounded-full bg-woot-50 text-sm font-medium text-woot-600"
                        >
                          {{ conversationInitial(c) }}
                        </div>
                      </td>
                      <td class="py-2 pr-2 align-middle">
                        <div class="min-w-0">
                          <p class="truncate font-medium text-slate-900 dark:text-slate-100">
                            {{ conversationTitle(c) }}
                          </p>
                          <p class="truncate text-xs text-slate-500 dark:text-slate-400">
                            {{
                              t('KANBAN.BULK_ACTIONS.SEND_MESSAGE.CONVERSATION_ID', {
                                id: c.id,
                              })
                            }}
                          </p>
                        </div>
                      </td>
                      <td class="truncate py-2 pr-2 align-middle text-slate-600 dark:text-slate-400">
                        {{ conversationInboxName(c) }}
                      </td>
                      <td class="py-2 pr-2 align-middle">
                        <div
                          v-if="conversationLabels(c).length"
                          class="flex min-w-0 flex-wrap gap-1"
                        >
                          <span
                            v-for="label in conversationLabels(c)"
                            :key="label"
                            class="max-w-full truncate rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-700 dark:bg-slate-800 dark:text-slate-300"
                          >
                            {{ label }}
                          </span>
                        </div>
                        <span
                          v-else
                          class="text-xs text-slate-400 dark:text-slate-500"
                        >
                          {{ t('MESSAGE_DISPATCHER.EMPTY_FIELD') }}
                        </span>
                      </td>
                      <td class="py-2 pr-2 align-middle">
                        <span
                          v-if="c.priority"
                          class="inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold"
                          :class="priorityClasses(c.priority)"
                        >
                          {{ priorityLabel(c.priority) }}
                        </span>
                        <span
                          v-else
                          class="text-xs text-slate-400 dark:text-slate-500"
                        >
                          {{ t('MESSAGE_DISPATCHER.EMPTY_FIELD') }}
                        </span>
                      </td>
                      <td class="py-2 pr-4 align-middle text-slate-500 dark:text-slate-500">
                        <TimeAgo
                          :last-activity-timestamp="c.last_activity_at"
                          :created-at-timestamp="c.created_at"
                        />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div
                class="flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 px-3 py-2 text-sm text-slate-600 dark:border-slate-700 dark:text-slate-400"
              >
                <span>
                  {{
                    t('MESSAGE_DISPATCHER.PAGINATION_STATUS', {
                      page: conversationPage,
                      total: conversationPageCount,
                    })
                  }}
                </span>
                <div class="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    color="slate"
                    size="sm"
                    :disabled="conversationPage <= 1 || loadingList"
                    @click="previousConversationPage"
                  >
                    {{ t('MESSAGE_DISPATCHER.PREVIOUS_PAGE') }}
                  </Button>
                  <Button
                    variant="ghost"
                    color="slate"
                    size="sm"
                    :disabled="
                      conversationPage >= conversationPageCount || loadingList
                    "
                    @click="nextConversationPage"
                  >
                    {{ t('MESSAGE_DISPATCHER.NEXT_PAGE') }}
                  </Button>
                </div>
              </div>
            </div>
            <div
              v-else-if="!loadingList"
              class="py-6 text-center text-sm text-n-slate-11"
            >
              {{ t('MESSAGE_DISPATCHER.EMPTY_CONVERSATIONS') }}
            </div>
          </div>
        </section>

        <aside class="flex min-h-0 flex-col gap-4 overflow-y-auto">
        <section class="space-y-4">
          <div class="flex items-center justify-between gap-3">
            <div class="min-w-0 flex flex-1 items-start gap-2">
              <div class="min-w-0">
                <p class="text-xs uppercase tracking-wide text-n-slate-11">
                  {{ t('MESSAGE_DISPATCHER.MESSAGE_SECTION') }}
                </p>
                <h4 class="text-lg font-semibold text-n-slate-12">
                  {{ t('MESSAGE_DISPATCHER.COMPOSER_TITLE') }}
                </h4>
                <p class="text-sm text-n-slate-11">
                  {{ t('MESSAGE_DISPATCHER.COMPOSER_DESCRIPTION') }}
                </p>
              </div>
            </div>
            <div class="flex shrink-0 items-center gap-2">
              <Button
                type="button"
                variant="outline"
                color="slate"
                size="sm"
                icon="i-lucide-braces"
                @click="openVariablesModal"
              >
                {{ t('MESSAGE_DISPATCHER.VARIABLES_BUTTON') }}
              </Button>
            </div>
          </div>

          <div class="space-y-4">
            <div class="editor-wrap w-full">
              <WootMessageEditor
                id="message-dispatcher-composer"
                v-model="composerMessage"
                class="message-editor min-h-[12rem] [&>div]:px-1"
                channel-type="Context::Default"
                enable-variables
                :focus-on-mount="false"
                :placeholder="t('MESSAGE_DISPATCHER.COMPOSER_PLACEHOLDER')"
              />
            </div>

            <div class="space-y-3">
              <div class="flex items-start justify-between gap-3">
                <div>
                  <p class="text-xs uppercase tracking-wide text-n-slate-11">
                    {{ t('MESSAGE_DISPATCHER.VARIATIONS_TITLE') }}
                  </p>
                  <p class="text-sm text-n-slate-11">
                    {{ t('MESSAGE_DISPATCHER.VARIATIONS_DESCRIPTION') }}
                  </p>
                </div>
                <Button
                  v-if="editingVariationIndex === null"
                  variant="outline"
                  color="blue"
                  size="sm"
                  @click="handleAddVariation"
                >
                  {{ t('MESSAGE_DISPATCHER.ADD_VARIATION') }}
                </Button>
              </div>

              <div v-if="messageVariations.length > 0" class="space-y-2">
                <div
                  v-for="(variation, index) in messageVariations"
                  :key="index"
                  class="flex items-start gap-3 rounded-xl border border-n-weak bg-n-alpha-1 p-3"
                >
                  <div class="min-w-0 flex-1">
                    <div class="mb-1 text-xs font-semibold text-n-slate-11">
                      {{ t('MESSAGE_DISPATCHER.VARIATION') }} {{ index + 1 }}
                    </div>
                    <p class="mb-0 line-clamp-3 text-sm text-n-slate-12">
                      {{ plainTextFromHtml(variation) }}
                    </p>
                  </div>
                  <div class="flex shrink-0 gap-2">
                    <Button
                      variant="ghost"
                      color="slate"
                      size="xs"
                      @click="handleEditVariation(index)"
                    >
                      {{ t('KANBAN.BULK_ACTIONS.EDIT') }}
                    </Button>
                    <Button
                      variant="ghost"
                      color="ruby"
                      size="xs"
                      @click="handleRemoveVariation(index)"
                    >
                      {{ t('KANBAN.BULK_ACTIONS.DELETE') }}
                    </Button>
                  </div>
                </div>
              </div>

              <div
                v-if="editingVariationIndex !== null"
                class="space-y-3 rounded-xl border border-n-brand bg-n-alpha-1 p-4"
              >
                <label
                  class="block text-sm font-semibold text-n-slate-12"
                  for="message-dispatcher-variation"
                >
                  {{
                    editingVariationIndex === messageVariations.length
                      ? t('MESSAGE_DISPATCHER.ADD_VARIATION')
                      : t('MESSAGE_DISPATCHER.EDIT_VARIATION')
                  }}
                </label>
                <WootMessageEditor
                  id="message-dispatcher-variation"
                  v-model="variationInput"
                  class="message-editor min-h-[10rem] [&>div]:px-1"
                  channel-type="Context::Default"
                  enable-variables
                  :focus-on-mount="false"
                  :placeholder="t('MESSAGE_DISPATCHER.VARIATION_PLACEHOLDER')"
                />
                <div class="flex justify-end gap-2">
                  <Button
                    variant="ghost"
                    color="slate"
                    size="sm"
                    @click="handleCancelVariation"
                  >
                    {{ t('KANBAN.BULK_ACTIONS.CANCEL') }}
                  </Button>
                  <Button
                    variant="solid"
                    color="blue"
                    size="sm"
                    @click="handleSaveVariation"
                  >
                    {{ t('KANBAN.BULK_ACTIONS.SAVE') }}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section
          v-if="!sendNow"
          class="space-y-4 rounded-xl border bg-n-solid-1 p-4 shadow-sm transition-shadow"
          :class="
            highlightedStep === 3
              ? 'border-n-brand ring-2 ring-n-brand'
              : 'border-n-weak'
          "
        >
          <div class="flex items-start justify-between gap-3">
            <div class="min-w-0 flex flex-1 items-start gap-2">
              <span
                class="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold text-white"
                :class="{ 'bg-n-brand': !step3Done }"
                :style="
                  step3Done ? { backgroundColor: STEP_COMPLETED_NUMBER_BG } : {}
                "
              >
                <fluent-icon v-if="step3Done" icon="checkmark" size="14" />
                <template v-else>3</template>
              </span>
              <div class="min-w-0">
                <p class="text-xs uppercase tracking-wide text-n-slate-11">
                  {{ t('MESSAGE_DISPATCHER.SCHEDULE_SECTION') }}
                </p>
                <h4 class="text-lg font-semibold text-n-slate-12">
                  {{ t('MESSAGE_DISPATCHER.SCHEDULE_DATETIME') }}
                </h4>
                <p class="text-sm text-n-slate-11">
                  {{ t('MESSAGE_DISPATCHER.SCHEDULE_HELP') }}
                </p>
              </div>
            </div>
          </div>
          <div>
            <label class="mb-2 block text-sm font-semibold text-n-slate-12">
              {{ t('MESSAGE_DISPATCHER.SCHEDULE_DATETIME') }}
            </label>
            <input
              v-model="scheduledTime"
              type="datetime-local"
              class="w-full rounded-xl border border-n-weak bg-n-solid-1 text-n-slate-12 p-3 text-sm"
            />
          </div>
        </section>

        </aside>
      </div>
    </div>

    <Dialog
      ref="filtersDialogRef"
      width="lg"
      :title="t('MESSAGE_DISPATCHER.FILTERS_MODAL.TITLE')"
      :description="t('MESSAGE_DISPATCHER.FILTERS_MODAL.INTRO')"
      :confirm-button-label="t('MESSAGE_DISPATCHER.FILTERS_MODAL.APPLY')"
      :cancel-button-label="t('MESSAGE_DISPATCHER.FILTERS_MODAL.CLOSE')"
      @confirm="closeFiltersModal"
    >
      <div class="grid gap-4">
        <label class="grid gap-2 text-sm font-medium text-n-slate-12">
          {{ t('MESSAGE_DISPATCHER.FILTERS_MODAL.CONVERSATION') }}
          <input
            v-model="conversationFilters.query"
            type="text"
            class="m-0 w-full rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12 outline-none focus-visible:ring-2 focus-visible:ring-n-brand"
            :placeholder="t('MESSAGE_DISPATCHER.FILTERS_MODAL.CONVERSATION_PLACEHOLDER')"
          >
        </label>

        <label class="grid gap-2 text-sm font-medium text-n-slate-12">
          {{ t('MESSAGE_DISPATCHER.CHANNEL_COLUMN') }}
          <select
            v-model="conversationFilters.channel"
            class="m-0 w-full rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12 outline-none focus-visible:ring-2 focus-visible:ring-n-brand"
          >
            <option value="">
              {{ t('MESSAGE_DISPATCHER.FILTERS_MODAL.ALL') }}
            </option>
            <option
              v-for="channel in channelFilterOptions"
              :key="channel"
              :value="channel"
            >
              {{ channel }}
            </option>
          </select>
        </label>

        <label class="grid gap-2 text-sm font-medium text-n-slate-12">
          {{ t('MESSAGE_DISPATCHER.LABELS_COLUMN') }}
          <select
            v-model="conversationFilters.label"
            class="m-0 w-full rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12 outline-none focus-visible:ring-2 focus-visible:ring-n-brand"
          >
            <option value="">
              {{ t('MESSAGE_DISPATCHER.FILTERS_MODAL.ALL') }}
            </option>
            <option
              v-for="label in labelFilterOptions"
              :key="label"
              :value="label"
            >
              {{ label }}
            </option>
          </select>
        </label>

        <label class="grid gap-2 text-sm font-medium text-n-slate-12">
          {{ t('MESSAGE_DISPATCHER.PRIORITY_COLUMN') }}
          <select
            v-model="conversationFilters.priority"
            class="m-0 w-full rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12 outline-none focus-visible:ring-2 focus-visible:ring-n-brand"
          >
            <option value="">
              {{ t('MESSAGE_DISPATCHER.FILTERS_MODAL.ALL') }}
            </option>
            <option
              v-for="priority in priorityFilterOptions"
              :key="priority"
              :value="priority"
            >
              {{ priorityLabel(priority) }}
            </option>
          </select>
        </label>
      </div>

      <template #footer>
        <div class="flex w-full items-center justify-between gap-3">
          <Button
            variant="ghost"
            color="slate"
            size="sm"
            type="button"
            @click="clearConversationFilters"
          >
            {{ t('MESSAGE_DISPATCHER.FILTERS_MODAL.CLEAR') }}
          </Button>
          <Button
            variant="solid"
            color="blue"
            size="sm"
            type="button"
            @click="closeFiltersModal"
          >
            {{ t('MESSAGE_DISPATCHER.FILTERS_MODAL.APPLY') }}
          </Button>
        </div>
      </template>
    </Dialog>

    <Dialog
      ref="variablesDialogRef"
      width="2xl"
      overflow-y-auto
      :title="t('MESSAGE_DISPATCHER.VARIABLES_MODAL.TITLE')"
      :description="t('MESSAGE_DISPATCHER.VARIABLES_MODAL.INTRO')"
      :show-confirm-button="false"
      :cancel-button-label="t('MESSAGE_DISPATCHER.VARIABLES_MODAL.CLOSE')"
    >
      <div
        class="flex flex-col gap-6 max-h-[min(70vh,28rem)] overflow-y-auto pe-1"
      >
        <div class="space-y-3">
          <h4 class="text-sm font-semibold text-n-slate-12 mb-0">
            {{ t('MESSAGE_DISPATCHER.VARIABLES_MODAL.STANDARD_SECTION') }}
          </h4>
          <ul
            class="m-0 list-none divide-y divide-n-weak rounded-xl border border-n-weak bg-n-solid-1"
          >
            <li
              v-for="row in standardVariablesList"
              :key="row.key"
              class="px-4 py-3 flex flex-col gap-1"
            >
              <code class="text-sm font-mono text-n-brand break-all">
                {{ row.key }}
              </code>
              <span class="text-sm text-n-slate-11">{{ row.description }}</span>
            </li>
          </ul>
        </div>

        <div v-if="customVariablesList.length > 0" class="space-y-3">
          <h4 class="text-sm font-semibold text-n-slate-12 mb-0">
            {{ t('MESSAGE_DISPATCHER.VARIABLES_MODAL.CUSTOM_SECTION') }}
          </h4>
          <ul
            class="m-0 list-none divide-y divide-n-weak rounded-xl border border-n-weak bg-n-solid-1"
          >
            <li
              v-for="row in customVariablesList"
              :key="row.key"
              class="px-4 py-3 flex flex-col gap-1"
            >
              <code class="text-sm font-mono text-n-brand break-all">
                {{ row.key }}
              </code>
              <span class="text-sm text-n-slate-11">{{ row.description }}</span>
            </li>
          </ul>
        </div>

        <p class="text-xs text-n-slate-11 mb-0">
          {{ t('MESSAGE_DISPATCHER.VARIABLES_MODAL.EDITOR_HINT') }}
        </p>
      </div>
    </Dialog>
  </div>
</template>
